import clsx from 'clsx';
import gsap from 'gsap';
import { RefObject, useEffect, useRef, useState } from 'react';
import { Transition } from 'react-transition-group';
import isInViewport from '../utils/is-in-viewport';

export default function Accordion({
  id,
  title,
  close,
  className,
  checkbox = null,
  surplusHeight = 0,
  children,
  scrollableParent,
  setHeight,
  onClick,
}: React.PropsWithChildren<{
  id?: string;
  title: string;
  close?: boolean;
  checkbox?: React.ReactNode;
  className?: string;
  surplusHeight?: number;
  scrollableParent: RefObject<HTMLElement>;
  setHeight?: (id: string, height: number) => void;
  onClick?: (title: string) => void;
}>) {
  const [open, setOpen] = useState(false);
  const guid = crypto.randomUUID();
  const contentRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const initialHeight = useRef(undefined);

  const toggle = () => {
    setOpen(!open);
    onClick?.(title);
  };

  const getOffset = () => {
    const { top } = contentRef.current.getBoundingClientRect();
    const { scrollTop } = scrollableParent.current;

    return top + scrollTop;
  };

  const updateHeight = () => {
    const height = open
      ? (initialHeight.current || contentRef.current.scrollHeight) +
        surplusHeight
      : 0;

    gsap.to(contentRef.current, {
      minHeight: height,
    });

    if (!initialHeight.current)
      initialHeight.current = contentRef.current.scrollHeight;

    setHeight?.(id, height);
  };

  useEffect(() => {
    updateHeight();

    if (!open) return;
    // Scroll content into view if the content overflows the viewport
    const offsetPosition = getOffset();

    setTimeout(() => {
      // Use delay to allow accordion to grow to full size
      if (isInViewport(rootRef.current)) return;

      scrollableParent.current.scroll({
        top: offsetPosition - 60,
        behavior: 'smooth',
      });
    }, 500);
  }, [open]);

  useEffect(() => {
    if (surplusHeight !== undefined) {
      updateHeight();
    }
  }, [surplusHeight]);

  useEffect(() => {
    if (close) setOpen(false);
  }, [close]);

  useEffect(() => {
    initialHeight.current = contentRef.current.scrollHeight;
  }, []);

  return (
    <div className={clsx('vertical-accordion', className)} ref={rootRef}>
      <div className="vertical-accordion__head-wrapper">
        {checkbox}
        <button
          type="button"
          className="vertical-accordion__head"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={guid}
        >
          <h3 className="vertical-accordion__heading">{title}</h3>
          <div className={clsx('vertical-accordion__icon', { open })}></div>
        </button>
      </div>
      <div className="vertical-accordion__content" id={guid} ref={contentRef}>
        <Transition timeout={300} in={open} unmountOnExit>
          <div className="vertical-accordion__content-inner">{children}</div>
        </Transition>
      </div>
    </div>
  );
}
